import { REPORT_REGIONS } from "../data/reportsData"
import { buildDriveDownloadUrl } from "./driveLinks"

// ─────────────────────────────────────────────────────────────
// Parsing is two steps:
//   1. Pull the leading "<Month> <Day>" off the front of the name.
//   2. Search whatever text is left for any region's keywords
//      (see REPORT_REGIONS in data/reportsData.js). First match wins;
//      no match at all falls back to "global".
// ─────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

const DATE_PREFIX_PATTERN = new RegExp(
  `^(${MONTH_NAMES.join("|")})\\s+(\\d{1,2})`,
  "i"
)

// Filenames only carry month/day, never a year. We assume the current
// year unless that would put the date more than a week in the future
// (relative to `referenceDate`) — in which case it almost certainly
// belongs to last year's cycle, so we roll back a year.
export const resolveReportDate = (monthIndex, day, referenceDate = new Date()) => {
  const year = referenceDate.getFullYear()
  let date = new Date(year, monthIndex, day)

  const oneWeekMs = 7 * 24 * 60 * 60 * 1000
  if (date.getTime() - referenceDate.getTime() > oneWeekMs) {
    date = new Date(year - 1, monthIndex, day)
  }

  return date
}

export const toISODate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

// Extracts { monthIndex, day, restOfName } from the raw filename, or
// null if it doesn't start with a recognizable "<Month> <Day>" date.
const parseDatePrefix = (filename) => {
  const nameWithoutExt = filename.replace(/\.pdf$/i, "").trim()
  const match = nameWithoutExt.match(DATE_PREFIX_PATTERN)
  if (!match) return null

  const [fullMatch, monthName, dayStr] = match
  const monthIndex = MONTH_NAMES.findIndex(
    (m) => m.toLowerCase() === monthName.toLowerCase()
  )

  return {
    monthIndex,
    day: parseInt(dayStr, 10),
    restOfName: nameWithoutExt.slice(fullMatch.length),
  }
}

// Searches `text` for any region's keywords; falls back to "global"
// when nothing matches, per the rule: "if nothing is found then it's global".
export const detectRegion = (text) => {
  const normalized = text.toLowerCase()

  const match = REPORT_REGIONS.find(
    (r) => r.key !== "global" && r.keywords.some((kw) => normalized.includes(kw))
  )

  return match || REPORT_REGIONS.find((r) => r.key === "global")
}

export const formatFileSize = (bytes) => {
  const size = Number(bytes)
  if (!size) return "—"
  const mb = size / (1024 * 1024)
  if (mb < 0.1) return `${Math.max(1, Math.round(size / 1024))} KB`
  return `${mb.toFixed(1)} MB`
}


// Falls back to building the standard Drive download link from the file's ID
const resolveFileUrl = (driveFile) =>
  driveFile.webContentLink || driveFile.downloadUrl || buildDriveDownloadUrl(driveFile.id)

/**
 * Turns a raw file object returned by our backend's GET /drive/files
 * ({ id, name, size?, webContentLink? }) into a Report matching the
 * shape ReportCard/WeekAccordionItem expect. Returns null for files
 * that don't match the naming convention, so they can be filtered out
 * (and logged) upstream.
 */
export const parseReportFile = (driveFile) => {
  const parsed = parseDatePrefix(driveFile.name)

  if (!parsed) {
    console.warn(
      `[reports] Skipping "${driveFile.name}" — doesn't start with a ` +
        `recognizable "Month Day" date.`
    )
    return null
  }

  const { monthIndex, day, restOfName } = parsed
  const date = resolveReportDate(monthIndex, day)
  const region = detectRegion(restOfName)

  return {
    id: driveFile.id,
    weekOf: toISODate(date),
    key: region.key,
    title: region.title,
    summary: region.summary,
    color: region.color,
    icon: region.icon,
    fileSizeLabel: formatFileSize(driveFile.size),
    fileUrl: resolveFileUrl(driveFile),
    fileName: driveFile.name,
  }
}
